import { useEffect } from 'react';
import { isEmpty, isNil } from 'lodash';
import { MarketComparisonWritable } from 'api';
import { useFundInvestmentContext } from 'context';
import {
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NEW_MARKET_COMPARISON,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILES,
} from 'pages/FundInvestments/common/constants';
import {
  getInitialMarketAdjustment,
  matchesCapIqId,
  parsePublicCompsMarketComparison,
} from 'pages/FundInvestments/utils';
import { dbShortDate, getArrayValue, getObjectValue, getStringValue } from 'utilities';
import { UseFundInvestmentMarketAdjustmentSyncParams } from './useFundInvestmentMarketAdjustmentSync.types';

const useFundInvestmentMarketAdjustmentSync = (params: UseFundInvestmentMarketAdjustmentSyncParams) => {
  const {
    isLoading,
    marketAdjustmentFieldAttributes,
    marketAdjustmentForSheet,
    marketComparisonsForSheet,
    marketComparisonsRef,
    prevAsOfDateRef,
    prevMarketCurrencyRef,
    prevMarketDateRef,
    propertyUpdated,
    setCurrentAsOfDate,
    setCurrentPercentileSelection,
    setMarketAdjustmentForSheet,
    setMarketComparisonsForSheet,
  } = params;

  // Fund investments context
  const { fundInvestmentSpreadsheet, fundInvestmentValuationAllocation } = useFundInvestmentContext();
  const { currentAsOfDate, setCurrentMarketAdjustment, setCurrentMarketComparisons } = fundInvestmentSpreadsheet;
  const {
    currentMeasurementDate,
    fundInvestmentByAllocation,
    isLoadingFundInvestmentByAllocation,
    isLoadingValuationByAllocation,
    marketCurrency,
    marketDate,
    publicCompsByDate,
    valuationByAllocation,
    versionMarketCurrency,
    versionMarketDate,
  } = fundInvestmentValuationAllocation;

  // Set initial market adjustment, market comparisons, and percentile selection
  useEffect(() => {
    if (
      !isLoadingValuationByAllocation &&
      !isLoadingFundInvestmentByAllocation &&
      !isEmpty(valuationByAllocation) &&
      !isNil(fundInvestmentByAllocation)
    ) {
      const { asOfDate, marketAdjustment, marketComparisons, percentileSelection } = getInitialMarketAdjustment({
        currentMeasurementDate,
        fundInvestmentByAllocation,
        valuationByAllocation,
      });

      setCurrentAsOfDate(asOfDate);
      setCurrentPercentileSelection(percentileSelection);
      setMarketAdjustmentForSheet(marketAdjustment);
      setMarketComparisonsForSheet(marketComparisons);

      // Set initial as of date, market currency and market date references
      if (
        !isNil(asOfDate) &&
        !isNil(marketCurrency) &&
        !isNil(versionMarketCurrency) &&
        !isNil(marketDate) &&
        !isNil(versionMarketDate) &&
        isNil(prevAsOfDateRef.current) &&
        isNil(prevMarketCurrencyRef.current) &&
        isNil(prevMarketDateRef.current)
      ) {
        prevAsOfDateRef.current = dbShortDate(asOfDate);
        prevMarketCurrencyRef.current = getStringValue(versionMarketCurrency ?? marketCurrency);
        prevMarketDateRef.current = dbShortDate(versionMarketDate ?? marketDate);
      }
    }

    // Cleanup when dependencies change or on unmount
    return () => {
      setCurrentAsOfDate(null);
      setCurrentPercentileSelection(FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILES);
      setMarketAdjustmentForSheet(null);
      setMarketComparisonsForSheet(null);
    };
  }, [
    currentMeasurementDate,
    fundInvestmentByAllocation,
    isLoadingFundInvestmentByAllocation,
    isLoadingValuationByAllocation,
    marketCurrency,
    marketDate,
    prevAsOfDateRef,
    prevMarketCurrencyRef,
    prevMarketDateRef,
    setCurrentAsOfDate,
    setCurrentMarketAdjustment,
    setCurrentMarketComparisons,
    setCurrentPercentileSelection,
    setMarketAdjustmentForSheet,
    setMarketComparisonsForSheet,
    valuationByAllocation,
    versionMarketCurrency,
    versionMarketDate,
  ]);

  // Synchronize market adjustment and market comparisons with the sheet data
  useEffect(() => {
    setCurrentMarketAdjustment(marketAdjustmentForSheet);
    setCurrentMarketComparisons(marketComparisonsForSheet);

    // Cleanup when dependencies change or on unmount
    return () => {
      setCurrentMarketAdjustment(null);
      setCurrentMarketComparisons(null);
    };
  }, [marketAdjustmentForSheet, marketComparisonsForSheet, setCurrentMarketAdjustment, setCurrentMarketComparisons]);

  // Synchronize public comps when changing as of date or market date
  useEffect(() => {
    if (
      !isLoading &&
      !isNil(currentAsOfDate) &&
      !isNil(marketDate) &&
      !isNil(marketCurrency) &&
      !isNil(propertyUpdated) &&
      !isEmpty(publicCompsByDate)
    ) {
      let previousDate: string | null = null;
      let currentDate: string | null = null;

      // Determine which dates should be used to compare and update comparisons
      switch (propertyUpdated) {
        case 'asOfDate':
          previousDate = dbShortDate(prevAsOfDateRef.current);
          currentDate = dbShortDate(currentAsOfDate);
          break;

        case 'marketDate':
        case 'marketDateAndCurrency':
          previousDate = dbShortDate(prevMarketDateRef.current);
          currentDate = dbShortDate(marketDate);
          break;

        case 'currency':
          // Currency changes affect both as_of and market dates, so we use both here
          previousDate = dbShortDate(currentAsOfDate);
          currentDate = dbShortDate(marketDate);
          break;

        default:
      }

      // If previous or current date is null, do not proceed
      if (isNil(previousDate) || isNil(currentDate)) {
        return;
      }

      const { marketComparison: marketComparisonFieldAttributes } = marketAdjustmentFieldAttributes;

      const gpcResultsDates = Object.keys(publicCompsByDate);

      let updatedComparisons: MarketComparisonWritable[] = getArrayValue(marketComparisonsRef.current);

      // Track which CapIQ IDs have already been updated to prevent duplicates
      const updatedComparisonIds = new Set<string>();

      // Iterate through each Public comps date to update comparisons
      gpcResultsDates.forEach(gpcDate => {
        const gpcResults = getArrayValue(publicCompsByDate[gpcDate]);

        const gpcComparisons = gpcResults
          .map(publicComp =>
            parsePublicCompsMarketComparison({
              fieldAttributes: marketComparisonFieldAttributes,
              publicComp,
            })
          )
          .filter(Boolean) as MarketComparisonWritable[];

        // If no valid GPC comparisons are found, skip this date
        if (!gpcComparisons.length) {
          return;
        }

        updatedComparisons = updatedComparisons.map(currentComparison => {
          const {
            as_of_date: asOfDate,
            business_description: businessDescription,
            cap_iq_id: capIqId,
            company_name: companyName,
            company_type: companyType,
            currency,
            deleted_at: deletedAt,
            enabled,
            id,
            is_deleted: isDeleted,
            is_index: isIndex,
            market_adjustment: marketAdjustment,
            order,
            ticker_symbol: tickerSymbol,
            weight,
          } = getObjectValue(currentComparison);

          const currentCapIqId = getStringValue(capIqId);

          let shouldReplaceBasedOnDate = false;

          // Determine whether this comparison should be replaced based on the updated property
          switch (propertyUpdated) {
            case 'asOfDate':
            case 'marketDate':
              // Replace if the comparison's as_of_date matches the previous date
              // and is different from the current date
              shouldReplaceBasedOnDate =
                dbShortDate(asOfDate) === dbShortDate(previousDate) &&
                dbShortDate(asOfDate) !== dbShortDate(currentDate);
              break;

            case 'currency':
              // Replace if the comparison's as_of_date matches the current GPC date,
              // since currency changes affect both entries without modifying their dates.
              shouldReplaceBasedOnDate = dbShortDate(asOfDate) === dbShortDate(gpcDate);
              break;

            case 'marketDateAndCurrency':
              {
                const matchesPreviousButChanged =
                  dbShortDate(asOfDate) === dbShortDate(previousDate) &&
                  dbShortDate(asOfDate) !== dbShortDate(currentDate);

                const matchesCurrentGpcDate = dbShortDate(asOfDate) === dbShortDate(gpcDate);

                // Replace if the comparison's as_of_date matches the previous date and is different from the current date (market date)
                // or if the comparison's as_of_date matches the current GPC date (as of date)
                shouldReplaceBasedOnDate = matchesPreviousButChanged || matchesCurrentGpcDate;
              }
              break;

            default:
              break;
          }

          const hasNotBeenUpdated = !updatedComparisonIds.has(currentCapIqId);
          const isComparisonOutdated = shouldReplaceBasedOnDate && hasNotBeenUpdated;

          const comparisonToBeUpdated = gpcComparisons.find(matchesCapIqId(currentCapIqId));

          // If the comparison is outdated, update it
          if (isComparisonOutdated) {
            updatedComparisonIds.add(currentCapIqId);

            // Update comparison with updated public comp data
            if (comparisonToBeUpdated) {
              // Preserve existing editable fields while replacing outdated data
              return {
                ...comparisonToBeUpdated,
                deleted_at: deletedAt,
                enabled,
                id,
                is_deleted: isDeleted,
                market_adjustment: marketAdjustment,
                order,
                weight,
              };
            }

            // Reset comparison if no updated public comp data is found
            return {
              ...FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_NEW_MARKET_COMPARISON,
              as_of_date: currentDate,
              business_description: businessDescription,
              cap_iq_id: capIqId,
              company_name: companyName,
              company_type: companyType,
              currency,
              deleted_at: deletedAt,
              enabled,
              id,
              is_deleted: isDeleted,
              is_index: isIndex,
              market_adjustment: marketAdjustment,
              order,
              ticker_symbol: tickerSymbol,
              weight,
            };
          }

          // If the comparison is not outdated, keep the current one
          return currentComparison;
        });
      });

      setMarketComparisonsForSheet(updatedComparisons);

      // Update previous as of date, market currency and market date references
      prevAsOfDateRef.current = dbShortDate(currentAsOfDate);
      prevMarketCurrencyRef.current = getStringValue(marketCurrency);
      prevMarketDateRef.current = dbShortDate(marketDate);
    }
  }, [
    currentAsOfDate,
    isLoading,
    propertyUpdated,
    marketAdjustmentFieldAttributes,
    marketComparisonsRef,
    marketCurrency,
    marketDate,
    publicCompsByDate,
    setMarketComparisonsForSheet,
    prevAsOfDateRef,
    prevMarketCurrencyRef,
    prevMarketDateRef,
  ]);

  // Cleanup previous as of date, market currency and market date references on unmount
  useEffect(
    () => () => {
      prevAsOfDateRef.current = null;
      prevMarketCurrencyRef.current = null;
      prevMarketDateRef.current = null;
    },
    [prevAsOfDateRef, prevMarketCurrencyRef, prevMarketDateRef]
  );
};

export default useFundInvestmentMarketAdjustmentSync;
